import { toggleMark } from "prosemirror-commands";
import { MarkType, Schema } from "prosemirror-model";
import { useMemo } from "react";
import { Command, EditorState } from "prosemirror-state";
import { hasMarks } from "../schema";
import { isMarkActive } from "../utils";

export type BasicToolHandlers = {
	areSupportedBySchema: true;
	isBold: boolean;
	isItalic: boolean;
	isUnderline: boolean;
	isStrikeThrough: boolean;
	boldHandler(): void;
	italicHandler(): void;
	underlineHandler(): void;
	strikeThroughHandler(): void;
} | {
	areSupportedBySchema: false;
};

export function useBasicTools(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	focus?: () => void
): BasicToolHandlers {
	return useMemo(
		(): BasicToolHandlers => {
			const schema: Schema<never, never> = editorState.schema;

			const toggleMarkHandler = (markType: MarkType): void => {
				applyCommand(
					toggleMark(markType)
				);
				focus?.();
			};

			return hasMarks(
				schema,
				[
					"strong",
					"em",
					"underline",
					"strikeThrough"
				]
			)
				? {
					areSupportedBySchema: true,
					isBold: isMarkActive(editorState, schema.marks.strong),
					isItalic: isMarkActive(editorState, schema.marks.em),
					isUnderline: isMarkActive(editorState, schema.marks.underline),
					isStrikeThrough: isMarkActive(editorState, schema.marks.strikeThrough),
					boldHandler: () => toggleMarkHandler(schema.marks.strong),
					italicHandler: () => toggleMarkHandler(schema.marks.em),
					underlineHandler: () => toggleMarkHandler(schema.marks.underline),
					strikeThroughHandler: () => toggleMarkHandler(schema.marks.strikeThrough)
				}
				: {
					areSupportedBySchema: false
				};
		},
		[
			applyCommand,
			editorState,
			focus
		]
	);
}